import { useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Seller outreach burst modal.
 *
 * User flow:
 *  1. Pick mode:
 *     - Quick Sale  → recommends eBay Buy It Now, emails TOP N highest-max-price wanters (default 10)
 *     - Max Value   → recommends eBay Auction, emails ALL wanters for this barcode
 *  2. Paste eBay listing URL
 *  3. Confirm + send
 *
 * One burst per (seller, barcode) — backend 409s if already sent.
 */
export default function OutreachModal({ group, onClose, onSent }) {
  const total = group.matches.length;
  const [mode, setMode] = useState('quick_sale');
  const [topN, setTopN] = useState(Math.min(10, total));
  const [ebay, setEbay] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);

  const listingType = mode === 'quick_sale' ? 'buy_it_now' : 'auction';
  const targets =
    mode === 'quick_sale'
      ? group.matches.slice(0, Math.max(1, Math.min(topN, total)))
      : group.matches;

  const send = async (e) => {
    e.preventDefault();
    setErr('');
    setLoading(true);
    try {
      const body = {
        barcode: group.barcode,
        ebay_url: ebay.trim(),
        listing_type: listingType,
        mode,
        match_ids: mode === 'quick_sale' ? targets.map((m) => m.match_id) : null,
      };
      const res = await fetch(`${API}/api/wanted-list/outreach`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        setErr(data.detail || 'Could not send outreach.');
      } else {
        setResult(data);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.85)' }}
      data-testid="outreach-modal"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-[#0C0C0C] border border-zinc-800 rounded-2xl shadow-2xl p-6"
      >
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-xl font-extrabold text-white">Email wanters</h3>
          <span className="text-xs text-zinc-500 font-mono">{group.barcode}</span>
        </div>
        <p className="text-sm text-zinc-400 mb-5">
          <strong className="text-white">{group.title || 'this item'}</strong> · {total} interested buyer
          {total === 1 ? '' : 's'}
        </p>

        {result ? (
          <div className="text-center py-6" data-testid="outreach-result">
            <p className="text-2xl font-extrabold" style={{ color: '#00D26A' }}>
              ✓ Sent
            </p>
            <p className="text-zinc-300 mt-2">
              {result.sent} email{result.sent === 1 ? '' : 's'} sent
              {result.skipped > 0 && <span className="text-zinc-500"> · {result.skipped} skipped (opt-out / blocked)</span>}
            </p>
            <button
              onClick={onSent}
              className="mt-5 px-5 py-2 rounded-full font-bold text-sm"
              style={{ background: 'linear-gradient(90deg,#00D26A,#FFD700)', color: '#050505' }}
              data-testid="outreach-done-btn"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={send}>
            <div className="grid grid-cols-2 gap-2 mb-4">
              <button
                type="button"
                onClick={() => setMode('quick_sale')}
                className={`p-3 rounded-xl text-left border ${mode === 'quick_sale' ? 'border-[#00D26A] bg-[#00D26A]/10' : 'border-zinc-800 bg-zinc-900'}`}
                data-testid="mode-quick-sale"
              >
                <div className="font-bold text-white">Quick Sale</div>
                <div className="text-xs text-zinc-400 mt-1">eBay Buy It Now · email top {Math.min(topN, total)}</div>
              </button>
              <button
                type="button"
                onClick={() => setMode('max_value')}
                className={`p-3 rounded-xl text-left border ${mode === 'max_value' ? 'border-[#FFD700] bg-[#FFD700]/10' : 'border-zinc-800 bg-zinc-900'}`}
                data-testid="mode-max-value"
              >
                <div className="font-bold text-white">Max Value</div>
                <div className="text-xs text-zinc-400 mt-1">eBay Auction · email all {total}</div>
              </button>
            </div>

            {mode === 'quick_sale' && total > 1 && (
              <label className="block text-sm text-zinc-400 mb-3">
                Top N to email:{' '}
                <input
                  type="number"
                  min="1"
                  max={total}
                  value={topN}
                  onChange={(e) => setTopN(Number(e.target.value))}
                  className="w-20 bg-black border border-zinc-700 rounded px-2 py-1 text-white"
                  data-testid="outreach-topn-input"
                />
              </label>
            )}

            <label className="block text-sm text-zinc-400 mb-1">eBay listing URL</label>
            <input
              required
              value={ebay}
              onChange={(e) => setEbay(e.target.value)}
              placeholder="https://www.ebay.co.uk/itm/…"
              className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-[#00D26A] mb-4"
              data-testid="outreach-ebay-input"
            />

            <p className="text-[11px] text-zinc-500 mb-4 leading-relaxed">
              <strong>Heads up:</strong> Flipping Friends is informational only — all transactions,
              payments, postage and feedback happen on eBay. We never touch the money.
            </p>

            {err && (
              <p className="text-sm mb-3" style={{ color: '#E04A3F' }} data-testid="outreach-error">
                {err}
              </p>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-full font-bold text-sm border border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                data-testid="outreach-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || ebay.trim().length < 10}
                className="px-5 py-2 rounded-full font-bold text-sm disabled:opacity-50"
                style={{ background: 'linear-gradient(90deg,#00D26A,#FFD700)', color: '#050505' }}
                data-testid="outreach-send-btn"
              >
                {loading ? 'Sending…' : `Send to ${targets.length} wanter${targets.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
